import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import AttributeApi from "../Api/AttributeApi";
import ImageUploadApi from "../Api/ImageUploadApi";
import usePermissions from "../hook/usePermissions";
import ImageCropper from "./ImageCropper";
import { IMAGE_BASE_URL } from "../Config/Index";
import { identifyAverageColor } from "../helper/ColorIdentifier";

const AttributeFormLayer = () => {
  const { hasPermission } = usePermissions();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isView = location.pathname.includes("/view/");

  const [formData, setFormData] = useState({
    "name": "",
    "values": [{ name: "", image: null, preview: null, isFilter: false }],
    "displayName": "",
    "description": "",
    "status": true
  });

  const [cropperState, setCropperState] = useState({ show: false, image: null, index: null });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  useEffect(() => {
    if (id) fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    setLoading(true);
    const response = await AttributeApi.getById(id);
    if (response.status) {
      const data = response.response.data || response.response;
      setFormData({
        ...data,
        status: data.status !== undefined ? (data.status === 1 || data.status === "active" || data.status === true) : data.isActive !== undefined ? data.isActive : true,
        values: data.values ? data.values.map(v => ({
            ...v,
            preview: v.image ? `${IMAGE_BASE_URL}/${v.image.path || v.image}` : null,
            isFilter: v.isFilter ?? false
        })) : [{ name: "", image: null, preview: null, isFilter: false }]
      });
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : (selectedOption === false ? false : "") }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleValueChange = (index, value) => {
    const updated = [...formData.values];
    updated[index].name = value;
    setFormData(prev => ({ ...prev, values: updated }));
    
    if (errors.values && updated.every(v => v.name?.trim())) {
      setErrors(prev => {
        const nextErrors = { ...prev };
        delete nextErrors.values;
        return nextErrors;
      });
    }
  };

  const handleIsFilterChange = (index, value) => {
    const updated = [...formData.values];
    updated[index].isFilter = value;
    setFormData(prev => ({ ...prev, values: updated }));
  };

  const addValueField = () => {
    setFormData(prev => ({
        ...prev,
        values: [...prev.values, { name: "", image: null, preview: null, isFilter: false }]
    }));
  };

  const removeValueField = (index) => {
    const updated = [...formData.values];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, values: updated }));
  };

  const handleFileChange = (e, index) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setCropperState({ show: true, image: reader.result, index });
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onCropComplete = async (cropped) => {
    const updated = [...formData.values];
    updated[cropperState.index].blob = cropped.blob;
    updated[cropperState.index].preview = cropped.url;
    
    // Auto-detect color name if not already set
    if (!updated[cropperState.index].name || updated[cropperState.index].name === "") {
        const color = await identifyAverageColor(cropped.url);
        if (color && color.name) {
            updated[cropperState.index].name = color.name;
        }
    }

    setFormData(prev => ({ ...prev, values: updated }));
    setCropperState({ show: false, image: null, index: null });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Attribute Name is Required.";
    if (formData.values.some(v => !v.name?.trim())) newErrors.values = "Each value must have a name.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const finalValues = [];
    for (const val of formData.values) {
        let finalImage = val.image;
        if (val.blob) {
            const formDataUpload = new FormData();
            formDataUpload.append("file", val.blob, "swatch.jpg");
            const uploadRes = await ImageUploadApi.uploadImage({ formData: formDataUpload, path: "attributes" });
            if (uploadRes.status) {
                finalImage = uploadRes.response.data || uploadRes.response;
            }
        }
        finalValues.push({ _id: val._id, name: val.name, image: finalImage, isFilter: val.isFilter ?? false });
    }

    let payload = { ...formData, values: finalValues };
    payload.isActive = payload.status;
    let result;
    if (id) result = await AttributeApi.update(id, payload);
    else result = await AttributeApi.create(payload);
    setLoading(false);
    if (result.status) navigate("/attributes");
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24">
        <h6 className="text-primary-600 pb-2 mb-0">{isView ? "View" : id ? "Edit" : "Create"} Attributes</h6>
      </div>
      <div className="card-body p-24">
        <form onSubmit={handleSubmit} noValidate>
          <div className="row gy-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Attribute Name (e.g. Colors) <span className="text-danger">*</span></label>
              <input type="text" className={`form-control radius-8 ${errors.name ? 'is-invalid' : ''}`} name="name" value={formData.name} onChange={handleChange} placeholder="Enter Attribute Name" disabled={isView} />
              {errors.name && <small className="text-danger">{errors.name}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Status <span className="text-danger">*</span></label>
              <Select name="status" options={statusOptions} value={statusOptions.find((opt) => opt.value === formData.status)} onChange={handleSelectChange} styles={selectStyles(errors.status)} isDisabled={isView} isClearable={false} />
            </div>
            {/* <div className="col-md-6">
              <label className="form-label fw-semibold">Display Name</label>
              <input type="text" className="form-control radius-8" name="displayName" value={formData.displayName} onChange={handleChange} placeholder="Enter Display Name" disabled={isView} />
            </div> */}

            <div className="col-md-12">
                <div className="d-flex align-items-center justify-content-between mb-16">
                    <label className="form-label fw-bold mb-0">Attribute Values & Images</label>
                    {!isView && (
                        <button type="button" className="btn btn-primary-600 btn-sm radius-8 d-flex align-items-center gap-2" onClick={addValueField}>
                             <Icon icon="ic:baseline-plus" /> Add Value
                        </button>
                    )}
                </div>
                <div className="row gy-3">
                    {formData.values.map((val, index) => (
                        <div key={index} className="col-12 border p-12 radius-8 bg-light-50">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <label className="text-xs fw-semibold mb-1">Value Name (e.g. Red) <span className="text-danger">*</span></label>
                                    <input type="text" className={`form-control form-control-sm radius-8 ${errors.values && !val.name?.trim() ? 'is-invalid' : ''}`} value={val.name} onChange={(e) => handleValueChange(index, e.target.value)} placeholder="Value Name" disabled={isView} />
                                </div>
                                {/* <div className="col-md-4">
                                    <label className="text-xs fw-semibold mb-1">Swatch Image (Crop Enabled)</label>
                                    <div className="d-flex align-items-center gap-3">
                                        {!isView && <input type="file" className="form-control form-control-sm radius-8 flex-grow-1" accept="image/*" onChange={(e) => handleFileChange(e, index)} />}
                                        {val.preview && (
                                            <div className="border p-1 bg-white radius-4 shadow-sm">
                                                <img src={val.preview} alt="Swatch" className="radius-4" style={{ width: "32px", height: "32px", objectFit: "cover" }} />
                                            </div>
                                        )}
                                    </div>
                                </div> */}
                                <div className="col-md-3">
                                    <label className="text-xs fw-semibold mb-1 d-block">Is Filter</label>
                                    <div className="btn-group border radius-8 overflow-hidden" style={{ height: '36px' }}>
                                        <button
                                            type="button"
                                            onClick={() => !isView && handleIsFilterChange(index, true)}
                                            className={`btn btn-sm border-0 px-12 transition-all ${val.isFilter ? 'btn-primary-600' : 'bg-white text-secondary-400'}`}
                                            disabled={isView}
                                            style={{ fontSize: '10px', fontWeight: '800' }}
                                        >
                                            YES
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => !isView && handleIsFilterChange(index, false)}
                                            className={`btn btn-sm border-0 px-12 transition-all ${!val.isFilter ? 'btn-danger-600' : 'bg-white text-secondary-400'}`}
                                            disabled={isView}
                                            style={{ fontSize: '10px', fontWeight: '800' }}
                                        >
                                            NO
                                        </button>
                                    </div>
                                </div>
                                {!isView && formData.values.length > 1 && (
                                    <div className="col-md-3 text-end align-self-end mb-2">
                                        <button type="button" className="btn btn-danger-600 btn-sm radius-8 px-12 d-inline-flex align-items-center gap-2" onClick={() => removeValueField(index)} style={{ height: '36px' }}>
                                            <Icon icon="fluent:delete-24-regular" /> Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {errors.values && <small className="text-danger d-block mt-2">{errors.values}</small>}
                </div>
            </div>

            {/* <div className="col-md-12">
              <label className="form-label fw-semibold">Description</label>
              <textarea className="form-control radius-8" name="description" value={formData.description} onChange={handleChange} placeholder="Enter Description" disabled={isView} rows="2" />
            </div> */}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-24">
            <Link to="/attributes" className="btn btn-outline-danger-600 px-32 radius-8 justify-content-center" style={{ width: "120px" }}>Cancel</Link>
            {!isView && hasPermission("Attributes", id ? "edit" : "add") && (
              <button type="submit" className="btn btn-primary radius-8 px-18 py-11 justify-content-center" disabled={loading} style={{ backgroundColor: "#364e3d", borderColor: "#364e3d", width: "120px" }}>
                {loading ? "Saving..." : id ? "Update" : "Save"}
              </button>
            )}
          </div>
        </form>
      </div>
      {cropperState.show && (
        <ImageCropper 
            image={cropperState.image} 
            onCropComplete={onCropComplete} 
            onCancel={() => setCropperState({ show: false, image: null, index: null })} 
        />
      )}
    </div>
  );
};

export default AttributeFormLayer;

